import { useEffect, useState } from 'react'

import { AutocompleteCustomerContainer } from '@/components/AutocompleteCustomerContainer'
import type { SearchOption } from '@/components/ui/Search'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  resourceCountLabels,
  type ArchiveCustomerInput,
  type CustomerArchivePreview,
} from '@/db/domain/customerArchive'
import { useDb } from '@/lib/dbContext'
import { formatPocketBaseError } from '@/lib/pocketbaseErrors'

export interface ArchiveCustomerDialogProps {
  open: boolean
  customerId: string
  customerName: string
  onClose: () => void
  /** Called after the server accepted the archive. */
  onArchived?: () => void
}

function describeResources(preview: CustomerArchivePreview): string {
  const parts = resourceCountLabels.flatMap(([key, one, many]) => {
    const n = preview.counts[key] ?? 0
    return n > 0 ? [`${n} ${n === 1 ? one : many}`] : []
  })
  return parts.join(', ')
}

/**
 * Archive (soft delete) a customer. A reason is always required. When the
 * customer has services the dialog also demands a target customer, to which
 * those services — and optionally the notes, documents and revision trail —
 * are relinked in one server transaction.
 */
export function ArchiveCustomerDialog({
  open,
  customerId,
  customerName,
  onClose,
  onArchived,
}: ArchiveCustomerDialogProps) {
  const db = useDb()
  const [preview, setPreview] = useState<CustomerArchivePreview | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [target, setTarget] = useState<SearchOption | null>(null)
  const [moveNotes, setMoveNotes] = useState(true)
  const [moveDocuments, setMoveDocuments] = useState(true)
  const [moveRevisions, setMoveRevisions] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setPreview(null)
    setLoadError(null)
    setReason('')
    setTarget(null)
    setMoveNotes(true)
    setMoveDocuments(true)
    setMoveRevisions(true)
    setError(null)
    let cancelled = false
    db.previewCustomerArchive(customerId).then(
      (result) => {
        if (!cancelled) setPreview(result)
      },
      (err) => {
        if (!cancelled) {
          setLoadError(
            formatPocketBaseError(err, {
              fallbackMessage: 'Unable to check what this customer is linked to.',
            }),
          )
        }
      },
    )
    return () => {
      cancelled = true
    }
  }, [open, customerId, db])

  const hasServices = (preview?.services ?? 0) > 0
  const targetId = target?.id != null ? String(target.id) : ''
  const owned = [
    { key: 'notes', label: 'Notes', count: preview?.counts.customer_notes ?? 0, value: moveNotes, set: setMoveNotes },
    { key: 'documents', label: 'Documents', count: preview?.counts.customer_documents ?? 0, value: moveDocuments, set: setMoveDocuments },
    { key: 'revisions', label: 'Edit history', count: preview?.counts.customer_revisions ?? 0, value: moveRevisions, set: setMoveRevisions },
  ]

  const canSubmit =
    !!preview && !submitting && reason.trim().length > 0 && (!hasServices || !!targetId)

  const submit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    const input: ArchiveCustomerInput = {
      reason: reason.trim(),
      targetId: targetId || undefined,
      moveNotes,
      moveDocuments,
      moveRevisions,
    }
    try {
      await db.archiveCustomer(customerId, input)
      onArchived?.()
      onClose()
    } catch (err) {
      setError(
        formatPocketBaseError(err, { fallbackMessage: 'Unable to archive this customer.' }),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !submitting && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Archive {customerName}?</DialogTitle>
          <DialogDescription>
            Archived customers are hidden from search and pickers but nothing is
            deleted. You can restore them from the Archived tab.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {loadError ? <p className="text-rose-600">{loadError}</p> : null}
          {!preview && !loadError ? (
            <p className="text-slate-500">Checking what is linked to this customer…</p>
          ) : null}

          {preview && hasServices ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
              This customer has {describeResources(preview)}. Choose who they
              should be relinked to.
            </div>
          ) : null}

          {preview ? (
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">
                Reason <span className="text-rose-600">*</span>
              </span>
              <textarea
                aria-label="Reason for archiving"
                className="min-h-[72px] w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm"
                placeholder="e.g. Duplicate of the customer below"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
          ) : null}

          {preview ? (
            <div>
              <span className="mb-1 block text-xs font-medium text-slate-600">
                Relink to {hasServices ? <span className="text-rose-600">*</span> : '(optional)'}
              </span>
              <AutocompleteCustomerContainer
                value={target}
                onChange={setTarget}
                ariaLabel="Customer to relink to"
                excludeIds={[customerId]}
              />
            </div>
          ) : null}

          {preview && hasServices && targetId ? (
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Relinked services keep their own invoice details — nothing is
              rewritten, and invoiced records stay as invoiced. Each service
              will show where its invoice details differ from the new
              customer, so you can update them one at a time.
            </p>
          ) : null}

          {preview && targetId ? (
            <fieldset className="space-y-1.5">
              <legend className="mb-1 text-xs font-medium text-slate-600">
                Also move to the new customer
              </legend>
              {owned.map((item) => (
                <label key={item.key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={item.value}
                    onChange={(event) => item.set(event.target.checked)}
                  />
                  <span>
                    {item.label} <span className="text-slate-400">({item.count})</span>
                  </span>
                </label>
              ))}
            </fieldset>
          ) : null}

          {error ? <p className="text-rose-600">{error}</p> : null}
        </div>

        <DialogFooter>
          <button
            type="button"
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            onClick={onClose}
            disabled={submitting}
          >
            Keep customer
          </button>
          <button
            type="button"
            className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
            onClick={submit}
            disabled={!canSubmit}
          >
            {submitting ? 'Archiving…' : 'Archive customer'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
